import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, devError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  const fetchCart = useCallback(async () => {
    if (!user) {
      setItems([]);
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setItems(data || []);
    } catch (e) {
      // Don't toast on initial silent fetch (401 expected for unauthed)
      if (e?.response?.status !== 401) {
        devError("fetchCart failed:", formatApiError(e));
      }
    }
  }, [user]);

  const fetchWishlist = useCallback(async () => {
    if (!user) {
      setWishlist([]);
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setWishlist(data || []);
    } catch (e) {
      if (e?.response?.status !== 401) {
        devError("fetchWishlist failed:", formatApiError(e));
      }
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
    fetchWishlist();
  }, [fetchCart, fetchWishlist]);

  const addToCart = async (productId, quantity = 1) => {
    if (!user) {
      toast.error("Silakan login terlebih dahulu");
      return false;
    }
    try {
      const { data } = await api.post("/cart", { product_id: productId, quantity });
      setItems(data);
      toast.success("Ditambahkan ke keranjang");
      return true;
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menambah ke keranjang");
      return false;
    }
  };

  const updateQty = async (itemId, productId, quantity) => {
    try {
      const { data } = await api.put(`/cart/${itemId}`, { product_id: productId, quantity });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal memperbarui keranjang");
    }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/cart/${itemId}`);
      setItems((s) => s.filter((i) => i.id !== itemId));
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menghapus item");
    }
  };

  const toggleWishlist = async (productId) => {
    if (!user) {
      toast.error("Silakan login terlebih dahulu");
      return;
    }
    try {
      await api.post(`/wishlist/${productId}`);
      fetchWishlist();
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal memperbarui wishlist");
    }
  };

  const isWishlisted = (productId) => wishlist.some((p) => p.id === productId);

  const cartCount = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const cartTotal = items.reduce((s, i) => {
    const p = i.product || {};
    const price = p.sale_price || p.price || 0;
    return s + price * i.quantity;
  }, 0);

  return (
    <CartContext.Provider value={{
      items, wishlist, cartCount, cartTotal,
      addToCart, updateQty, removeItem,
      toggleWishlist, isWishlisted,
      fetchCart, fetchWishlist,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
